import React, { useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useFetchGuide } from './hook/useFetchGuide';
import Print from 'components/print/Print';
import { PrintConsumer } from 'components/print/hook/usePrint';
import { Divider, IconButton, styled, Typography } from '@mui/material';
import { Close, Print as PrintIcon } from '@mui/icons-material';
import history from 'services/history';
import { format } from 'date-fns';
import NoData from 'components/no-data/NoData';
import InsideLoading from 'components/loading/InsideLoading';
import SavePdfButton from 'components/print/SavePdfButton';
import { pdfFileName } from 'helpers/exportPdf';

const HeaderActions = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  top: 10,
  right: 10,
  position: 'absolute',
  '@media print': {
    display: 'none',
  },
});

const PrintPage = styled('div')({
  paddingBottom: 40,
  maxWidth: '300mm',
  minWidth: '110mm',
  position: 'relative',
  boxShadow: '0 0 8px rgba(0,0,0,0.1)',
  padding: 16,
  '@media print': {
    width: '110mm',
    minHeight: '297mm',
    boxShadow: 'none',
    padding: '10mm',
    '-webkit-print-color-adjust': 'exact',
    printColorAdjust: 'exact',
    '& p': {
      fontSize: 10,
      lineHeight: 1.2,
    },
    '& h6': {
      fontSize: 12,
      lineHeight: 1.2,
    },
  },
});

const Container = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  position: 'relative',
  '& li': {
    listStyleType: 'disc',
    marginLeft: 16,
  },
});

const Header = styled('div')({
  padding: 0,
  display: 'grid',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2,
  '@media print': {
    '&.logo': {
      marginTop: -50,
    },
    '&.infos': {
      display: 'none',
    },
  },
});

const Img = styled('img')({
  width: 200,
  height: 100,
  objectFit: 'cover',
  zIndex: 3,
  '@media print': {
    objectFit: 'cover',
  },
});

const Infos = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, 1fr)',
  gap: 8,
});

const Subtitle = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
});

const Box = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  maxWidth: 200,
  padding: 4,
  '& > .title': {
    color: theme.palette.primary.main,
  },
  '@media print': {
    padding: 2,
  },
}));

const PrintGuide: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { guide, loading } = useFetchGuide(id);
  const pageRef = useRef<HTMLDivElement>(null);
  // quem abriu pode pedir para voltar a outra tela (ex.: ficha do paciente)
  const returnTo = (history.location.state as { returnTo?: string } | null)?.returnTo;

  return (
    <Print
      onExited={() => {
        history.push(returnTo || `/guides`);
      }}
    >
      {loading ? (
        <InsideLoading />
      ) : guide ? (
        <PrintConsumer>
          {context => (
            <>
              <HeaderActions>
                <SavePdfButton targetRef={pageRef} filename={pdfFileName('Guia', guide.id, guide.patient?.name)} />
                <IconButton onClick={window.print}>
                  <PrintIcon fontSize="small" />
                </IconButton>
                <IconButton onClick={context.handleClose}>
                  <Close fontSize="small" />
                </IconButton>
              </HeaderActions>
              <PrintPage ref={pageRef}>
                <Container>
                  <Header className="infos">
                    <Typography variant="h6" align="center" fontWeight={600}>
                      Informações da consulta Nº {guide.id}
                    </Typography>
                  </Header>
                  <Header className="logo">
                    <Img
                      src="https://h2i.s3.sa-east-1.amazonaws.com/upload/images/170245202601026958246571cc3.png"
                      alt="Logo"
                    />
                  </Header>

                  <Subtitle>
                    <Typography variant="h6" align="center">
                      GUIA Nº {guide?.id}
                    </Typography>
                  </Subtitle>

                  <Typography variant="subtitle2" fontWeight={600}>
                    Informações do cliente
                  </Typography>

                  <Infos>
                    <Box>
                      <Typography variant="body2" className="title" fontWeight={600}>
                        Nome
                      </Typography>
                      <Typography variant="body2">{guide.patient?.name}</Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" className="title" fontWeight={600}>
                        CPF
                      </Typography>
                      <Typography variant="body2">{guide.patient?.document_number}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" className="title" fontWeight={600}>
                        Telefone
                      </Typography>
                      <Typography variant="body2">{guide.patient?.phone}</Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" className="title" fontWeight={600}>
                        Endereço
                      </Typography>
                      <Typography variant="body2">
                        {guide.patient?.address
                          ? `${guide.patient.address.city} - ${guide.patient.address.state}`
                          : 'Não informado'}
                      </Typography>
                    </Box>
                  </Infos>

                  <Divider />

                  <Typography variant="subtitle2" fontWeight={600}>
                    Informações da consulta
                  </Typography>
                  <Infos>
                    <Box>
                      <Typography variant="body2" className="title" fontWeight={600}>
                        Médico
                      </Typography>
                      <Typography variant="body2">{guide.doctor?.name}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" className="title" fontWeight={600}>
                        Endereço Médico
                      </Typography>
                      <Typography variant="body2">
                        {guide.doctor?.address
                          ? `${guide.doctor.address.street}, ${guide.doctor.address.street_number}. ${guide.doctor.address.neighborhood} - ${guide.doctor.address.city} - ${guide.doctor.address.state}. ${guide.doctor.address.zip_code}`
                          : 'Não informado'}
                      </Typography>
                    </Box>
                    <Box style={{ gridColumn: '1/3', maxWidth: '100%' }}>
                      <Typography variant="body2" className="title" fontWeight={600}>
                        Consulta com cirurgião
                      </Typography>
                      <Typography variant="body2">{guide.procedure?.name}</Typography>
                    </Box>
                    {guide.observation && (
                      <Box style={{ gridColumn: '1/3', maxWidth: '100%' }}>
                        <Typography variant="body2" className="title" fontWeight={600}>
                          Observação
                        </Typography>
                        <Typography variant="body2">{guide.observation}</Typography>
                      </Box>
                    )}
                  </Infos>

                  <Divider />

                  <Typography variant="subtitle2" fontWeight={600}>
                    Dados da consulta
                  </Typography>
                  <Infos>
                    <Box>
                      <Typography variant="body2" className="title" fontWeight={600}>
                        Forma de Pagamento
                      </Typography>
                      <Typography variant="body2">{guide.payment_methods?.[0]?.payment_method?.name}</Typography>
                    </Box>

                    <Box>
                      <Typography variant="body2" className="title" fontWeight={600}>
                        Realização
                      </Typography>
                      <Typography variant="body2">{format(new Date(guide.created_at), 'dd/MM/yyyy HH:mm')}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="body2" className="title" fontWeight={600}>
                        Data da consulta
                      </Typography>

                      <Typography variant="body2">
                        {format(new Date(guide.appointment_date), 'dd/MM/yyyy HH:mm')}
                      </Typography>
                    </Box>
                  </Infos>
                </Container>
              </PrintPage>
            </>
          )}
        </PrintConsumer>
      ) : (
        <NoData message="Guia não encontrada." />
      )}
    </Print>
  );
};

export default PrintGuide;
